import * as React from "react";

import {
  useProductListAutoInitialization,
  useProductListFredhopperRedirects,
  useProductListLoadingState,
  useProductListPresentationModel,
  useProductListState,
} from "../product-list-provider";

export enum RESULTS_PAGE_TO_PRESENT {
  UNDEFINED = "UNDEFINED",
  ZERO_RESULTS = "ZERO_RESULTS",
  PRODUCT_LIST = "PRODUCT_LIST",
}

export type PresentedPage = `${RESULTS_PAGE_TO_PRESENT}`;

interface PlpPresenterForRoutingTheResultsPageConsumerInterface {
  pageToPresent: PresentedPage;
}

const defaultValue = {};

const PlpPresenterForRoutingTheResultsPageContext = React.createContext(
  defaultValue as unknown as PlpPresenterForRoutingTheResultsPageConsumerInterface
);

export const PlpPresenterForRoutingTheResultsPageProvider: React.FunctionComponent =
  (props) => {
    const { isLoading: isTheProductListLoading } = useProductListLoadingState();
    const { hasInitialized: hasTheProductsListBeenInitialized } =
      useProductListAutoInitialization();
    const { isRedirecting } = useProductListFredhopperRedirects();
    const { selectedOptions } = useProductListState();
    const { presentationProducts } = useProductListPresentationModel();

    const pageToPresent = React.useMemo(() => {
      const areThereNoProducts_and_areThereNoFilters =
        presentationProducts.length < 1 && selectedOptions.length < 1;
      const areThereProducts_orFiltersApplied =
        !areThereNoProducts_and_areThereNoFilters;
      const shouldWeShowTheResultsPageNow_potentiallyShowZeroResultsForTheFiltersApplied =
        hasTheProductsListBeenInitialized &&
        areThereProducts_orFiltersApplied &&
        !isRedirecting;

      const theProductListIsntLoading = !isTheProductListLoading;
      const isThisACompleteState_notAWorkInProgress =
        hasTheProductsListBeenInitialized && theProductListIsntLoading;
      const shouldWeShowTheFullPageZeroResultsCampaign =
        !shouldWeShowTheResultsPageNow_potentiallyShowZeroResultsForTheFiltersApplied &&
        isThisACompleteState_notAWorkInProgress &&
        !isRedirecting;

      if (
        shouldWeShowTheResultsPageNow_potentiallyShowZeroResultsForTheFiltersApplied
      ) {
        return RESULTS_PAGE_TO_PRESENT.PRODUCT_LIST;
      } else if (shouldWeShowTheFullPageZeroResultsCampaign) {
        return RESULTS_PAGE_TO_PRESENT.ZERO_RESULTS;
      }
      return RESULTS_PAGE_TO_PRESENT.UNDEFINED;
    }, [
      presentationProducts,
      selectedOptions,
      hasTheProductsListBeenInitialized,
      isTheProductListLoading,
    ]);

    const value: PlpPresenterForRoutingTheResultsPageConsumerInterface =
      React.useMemo(
        () => ({
          pageToPresent,
        }),
        [pageToPresent]
      );

    return (
      <PlpPresenterForRoutingTheResultsPageContext.Provider value={value}>
        {props.children}
      </PlpPresenterForRoutingTheResultsPageContext.Provider>
    );
  };

export const usePlpPresenterForRoutingTheResultsPage = () => {
  const value = React.useContext(PlpPresenterForRoutingTheResultsPageContext);
  if (value === defaultValue) {
    throw new Error(
      "usePlpPresenterForRoutingTheResultsPage must be used within a PlpPresenterForRoutingTheResultsPageProvider"
    );
  }
  return value;
};
